/**
 * Tracks user acquisition source (UTMs) via Local Storage.
 * Stores both the immutable "First Touch" and mutable "Current/Last Touch".
 * Tracks cumulative page views and session counts.
 */

// Local Storage object
interface UserMeta {
  first_seen?: string;
  last_seen?: string | number;
  visits?: number;
  pages?: number;
  [key: string]: string | number | undefined;
}

const STORAGE_KEY = 'user_meta';
const SESSION_DURATION_MS = 30 * 60 * 1000; // Session duration of 30 minutes

// UTM parameters
const UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
];

/**
 * Parse URL query parameters into a simple object
 */
function getQueryParams(): Record<string, string> {
  const params: Record<string, string> = {};

  if (typeof window === 'undefined') return params;

  const search = window.location.search.substring(1);
  if (!search) return params;

  const pairs = search.split('&');
  for (let i = 0; i < pairs.length; i++) {
    const pair = pairs[i].split('=');
    if (pair[0]) {
      params[decodeURIComponent(pair[0])] = decodeURIComponent(pair[1] || '');
    }
  }
  return params;
}

/**
 * Helper: Generate Date in format m/d/Y g:i:s A (e.g., "01/07/2026 1:19:24 PM")
 */
function getCurrentFormattedDate(): string {
  const date = new Date();

  const Y = date.getFullYear();
  const M = String(date.getMonth() + 1).padStart(2, '0');
  const D = String(date.getDate()).padStart(2, '0');

  // 24-hour format (00-23)
  const H = String(date.getHours()).padStart(2, '0');
  const m = String(date.getMinutes()).padStart(2, '0');
  const s = String(date.getSeconds()).padStart(2, '0');

  // Milliseconds (000-999)
  const ms = String(date.getMilliseconds()).padStart(3, '0');

  return `${Y}-${M}-${D} ${H}:${m}:${s}.${ms}`;
}

/*
 * Helper: Parse the specific "YYYY-MM-DD HH:mm:ss.sss" format back to a Timestamp (ms)
 * Handles legacy numbers and legacy string formats safely.
 */
function parseFormattedDate(dateStr: string | number | undefined): number {
  if (!dateStr) return 0;

  // Handle Legacy Number (Timestamp)
  if (typeof dateStr === 'number') {
    return dateStr;
  }

  // Safety check for strings
  if (typeof dateStr !== 'string') {
    return 0;
  }

  // Handle New Format: "2026-01-20 20:43:14.000" (similar to last_mode_ts)
  if (dateStr.includes('-')) {
    const isoStr = dateStr.replace(' ', 'T');
    const parsed = Date.parse(isoStr);

    if (!isNaN(parsed)) {
      return parsed;
    }
  }

  // Handle Legacy Format
  if (dateStr.includes('/')) {
    const parts = dateStr.split(' ');
    if (parts.length >= 2) {
      const dateParts = parts[0].split('/');
      const timeParts = parts[1].split(':');
      const meridiem = parts[2];

      if (dateParts.length === 3 && timeParts.length === 3) {
        const month = parseInt(dateParts[0], 10) - 1;
        const day = parseInt(dateParts[1], 10);
        const year = parseInt(dateParts[2], 10);
        let hours = parseInt(timeParts[0], 10);
        const minutes = parseInt(timeParts[1], 10);
        const seconds = parseInt(timeParts[2], 10);

        if (meridiem === 'PM' && hours !== 12) hours += 12;
        else if (meridiem === 'AM' && hours === 12) hours = 0;

        return new Date(year, month, day, hours, minutes, seconds).getTime();
      }
    }
  }

  return 0;
}

/**
 * Main UTM Tracking Function
 */
export function utmTracker(): void {
  if (typeof window === 'undefined') return;

  const queryParams = getQueryParams();
  const now = Date.now();
  const formattedDate = getCurrentFormattedDate();

  // Retrieve existing object or create a new one
  let userData: UserMeta = {};
  try {
    const existing = localStorage.getItem(STORAGE_KEY);
    if (existing) {
      userData = JSON.parse(existing);
    }
  } catch (e) {
    console.error('Error parsing user_meta from localStorage', e);
    userData = {};
  }

  // Initialize Counters if missing
  if (userData.visits === undefined) userData.visits = 0;
  if (userData.pages === undefined) userData.pages = 0;

  // Handle Session Logic (Visits)
  // Retrieve the last seen time by parsing the stored string (or number)
  const lastSeenTs = parseFormattedDate(userData.last_seen);

  // If lastSeenTs is 0, it means it's a first visit or parsing failed -> New Session.
  // Otherwise, check if 30 mins have passed.
  const isNewSession = lastSeenTs === 0 || (now - lastSeenTs) > SESSION_DURATION_MS;

  if (isNewSession) {
    userData.visits++;
  }

  // Handle Pageviews, always increment on every execution (page load)
  userData.pages++;

  // Update Timestamp (Stored as readable string only)
  userData.last_seen = formattedDate;

  // Set first_seen timestamp if strictly missing
  if (!userData.first_seen) {
    userData.first_seen = formattedDate;
  }

  // Set first_utms if missing
  UTM_KEYS.forEach((key) => {
    const firstKey = `first_${key}`;
    // If we have a value in the URL AND we haven't saved a "first" value yet:
    if (queryParams[key] && !userData[firstKey]) {
      userData[firstKey] = queryParams[key];
    }
  });

  // Handle "Current/Last Touch" (Mutable) - Always overwrites with latest URL params
  UTM_KEYS.forEach((key) => {
    if (queryParams[key]) {
      userData[key] = queryParams[key];
    }
  });

  // Save back to Local Storage
  localStorage.setItem(STORAGE_KEY, JSON.stringify(userData));
}
